/* eslint-disable max-lines */

import React, {
 useState, useRef, useEffect, useContext
} from 'react';
import {
  Tooltip, TooltipProvider, TooltipTrigger, TooltipContent
} from 'shared/components/ui/tooltip';
import {
 Label
} from 'shared/components/ui/label';
import {
 Input
} from 'shared/components/ui/input';
import {
 Textarea
} from 'shared/components/ui/textarea';
import {
 Button
} from 'shared/components/ui/button';
import {
 Checkbox
} from 'shared/components/ui/checkbox';
import {
 Alert, AlertDescription, AlertTitle
} from 'shared/components/ui/alert';
import {
 CheckCircle2, XCircle, Loader2, Save, Settings, X, Globe, FileText, Database
} from 'lucide-react';
import {
  SiPostgresql, SiMysql, SiMicrosoftsqlserver, SiMongodb, SiRedis, SiOracle, SiApachekafka, SiGraphql,
  SiElasticsearch, SiMilvus
} from 'react-icons/si';
import {
 connectionService
} from 'shared/api/services/BEX';
import {
 useParams
} from 'react-router-dom';
import {
 Context
} from 'shared/context/GlobalState';
import {
 Info
} from 'lucide-react';
import {
 RestApiFields
} from './Components/RestApiFields';
import {
 NoSqlDbFields
} from './Components/NoSqlDbFields';

import {
 RelationalDbFields
} from './Components/RelationalDbFields';
import {
 VectorSearchDbFields
} from './Components/VectorSearchDbFields';
import {
 Connection
} from './Components/Types/ConnectionTypes';

interface SaveResult {
  status: 'idle' | 'success' | 'error' | 'saving';
  message: string;
}

interface TestResult {
  status: 'idle' | 'testing' | 'success' | 'error';
  message: string;
}

interface ConnectionFormProps {
  onConnectionSaved: (data: any) => void;
  onCancel: () => void;
  editingConnection?: Connection;
  initialValues?: Partial<Connection>;
  successBanner?: string;
}

export const ConnectionForm: React.FC<ConnectionFormProps> = ({
  onConnectionSaved, onCancel, editingConnection, initialValues, successBanner
}) => {
  const [dataSourceType, setDataSourceType] = useState<string>(
    editingConnection?.source_type || initialValues?.source_type || ''
  );
  const [saveResult, setSaveResult] = useState<SaveResult>({
    status: 'idle',
    message: ''
  });
  const [testResult, setTestResult] = useState<TestResult>({
    status: 'idle',
    message: ''
  });
  const formRef = useRef<HTMLFormElement | null>(null);
  const {
 appId, oragID
} = useParams();
  const {
 addNewNotifcation
} = useContext(Context);

// removed unused testingConnections state, as it caused husky error
 const [, setTestingConnections] = useState<Set<string>>(new Set());

  useEffect(() => {
    if (editingConnection) {
      setDataSourceType(editingConnection.source_type);
    } else if (initialValues?.source_type) {
      setDataSourceType(initialValues.source_type);
    } else {
      setDataSourceType('');
      setTestResult({
        status: 'idle',
        message: ''
      });
      setSaveResult({
        status: 'idle',
        message: ''
      });
      formRef.current?.reset();
    }
  }, [editingConnection, initialValues]);

    const handleTestConnection = async (connection: Connection) => {
    const connectionId = connection?.connection_id;
    const orgId = connection?.orgId;
    const appId = connection?.appId;

    if (!connectionId || !orgId || !appId) return;

    // Add connectionId to the testing set
    setTestingConnections((prev) => new Set(prev).add(connectionId));

    try {
        // Call the testSingleConnection API
        await connectionService.testSingleConnection(orgId, appId, connectionId);

        // Handle the result if necessary

        // Optionally add a success notification or update state based on result
        addNewNotifcation('Connection tested successfully', 'success');
    } catch (error) {
        // Handle error from API call
        console.error('Test connection failed:', error);
        // Optionally add an error notification
        addNewNotifcation('Test connection failed. Please try again later.', 'error');
    } finally {
        // Remove connectionId from the testing set after the operation completes
        setTestingConnections((prev) => {
            const newSet = new Set(prev);
            newSet.delete(connectionId);
            return newSet;
        });
    }
};
   useEffect(() => {
    //
  }, [onConnectionSaved]);

  const handleSave = async (event: React.FormEvent) => {
    event.preventDefault();
    setSaveResult({
      status: 'saving',
      message: 'Saving configuration...'
    });

    const formData = new FormData(event.target as HTMLFormElement);
    const rawData = Object.fromEntries(formData.entries());

    const general = {
        org_id: oragID,
        app_id: appId,
        connection_name: String(rawData['connection-name']),
        description: String(rawData['description'] || ''),
        source_type: dataSourceType,
    };

    const settings: { [key: string]: any } = {};
    const advancedOptions: { [key: string]: any } = {};

    const jsonKeys = ['headers', 'query_params', 'body_template'];
    const numericSettingKeys = ['port', 'db', 'connection_timeout', 'timeout', 'max_connections'];
    const booleanSettingKeys = ['tls', 'ssl', 'encrypt', 'secure'];
    const arraySettingKeys = ['hosts'];

    Object.entries(rawData).forEach(([key, value]) => {
  if (!value) return;

  const normalizedKey = key.replace(/-/g, '_');

  if (['connection-name', 'description', 'source_type'].includes(key)) {
    return;
  }

  if (jsonKeys.includes(normalizedKey)) {
    if (typeof value === 'string') {
      try {
        settings[normalizedKey] = JSON.parse(value);
      } catch {
        settings[normalizedKey] = {};
      }
    } else {
      settings[normalizedKey] = {};
    }
  } else if (
    ['retry_attempts', 'retry_backoff', 'connection_pool_size'].includes(normalizedKey)
  ) {
    const finalKey =
      normalizedKey === 'retry_backoff'
        ? 'retry_backoff_secs'
        : normalizedKey;
    advancedOptions[finalKey] = Number(value);
  } else if (normalizedKey === 'logging') {
    advancedOptions.enable_logging = true;
  } else if (normalizedKey === 'cache_responses') {
    advancedOptions.cache_responses = true;
  } else if (arraySettingKeys.includes(normalizedKey)) {
    settings[normalizedKey] = String(value).split(',').map((s) => s.trim()).filter(Boolean);
  } else if (numericSettingKeys.includes(normalizedKey)) {
    settings[normalizedKey] = Number(value);
  } else if (booleanSettingKeys.includes(normalizedKey)) {
    settings[normalizedKey] = value === 'on' || value === 'true';
  } else {
    settings[normalizedKey] = value;
  }
});

    if (!('enable_logging' in advancedOptions)) {
      advancedOptions.enable_logging = false;
    }
    if (!('cache_responses' in advancedOptions)) {
      advancedOptions.cache_responses = false;
    }

    // Ensure unchecked boolean fields default to false
    if (dataSourceType === 'mongodb' && !('tls' in settings)) {
      settings.tls = false;
    }
    if (dataSourceType === 'redis' && !('ssl' in settings)) {
      settings.ssl = false;
    }
    if (dataSourceType === 'milvus' && !('secure' in settings)) {
      settings.secure = false;
    }

    // Ensure unchecked boolean fields default to false
    if (dataSourceType === 'mongodb' && !('tls' in settings)) {
      settings.tls = false;
    }
    if (dataSourceType === 'redis' && !('ssl' in settings)) {
      settings.ssl = false;
    }
    if (dataSourceType === 'milvus' && !('secure' in settings)) {
      settings.secure = false;
    }

    const dataToSave = {
      ...general,
      settings,
      advanced_options: advancedOptions,
    };

  try {
  let response;
  if (editingConnection) {
    response = await connectionService.updateConnection(
      editingConnection.connection_id,
      dataToSave
    );

    const updatedConnection = response?.data?.data ?? response?.data;

    addNewNotifcation(
      `Connection ${updatedConnection.connection_name} updated successfully!`,
      'success'
    );

    // ✅ Pass the updated connection object properly
    onConnectionSaved(updatedConnection);

    setSaveResult({
      status: 'success',
      message: `Connection ${updatedConnection.connection_name} updated successfully!`,
    });

  } else {
    response = await connectionService.createConnection(dataToSave);
    const newConnection = response?.data?.data ?? response?.data;

    addNewNotifcation(
      `Connection ${newConnection.connection_name} created successfully!`,
      'success'
    );

    onConnectionSaved(newConnection); // ✅ Immediately pass correct data
    setSaveResult({
      status: 'success',
      message: `Connection ${newConnection.connection_name} created successfully!`,
    });
  }
} catch (error: any) {
  console.error(error);
  setSaveResult({
    status: 'error',
    message: 'Failed to save connection.',
  });
  addNewNotifcation(error.message || 'Failed to save connection.', 'danger');
}

  };

  // Merge editingConnection with initialValues for field pre-fill
  const effectiveConnection = editingConnection || (initialValues ? {
 settings: initialValues.settings
} as any : undefined);

  const renderConnectionDetails = () => {
  if (!dataSourceType) return null;

  const relationalDbTypes = ['postgresql', 'mysql', 'sqlserver'] as const;
  const noSqlDbTypes = ['mongodb', 'redis'] as const;
  const restApi = ['rest'] as const;
  const vectorSearchTypes = ['elastic', 'milvus'] as const;

  if (relationalDbTypes.includes(dataSourceType as typeof relationalDbTypes[number])) {
    return <RelationalDbFields type={dataSourceType as typeof relationalDbTypes[number]} connection={effectiveConnection} />;
  }
  if (noSqlDbTypes.includes(dataSourceType as typeof noSqlDbTypes[number])) {
    return <NoSqlDbFields type={dataSourceType as typeof noSqlDbTypes[number]} connection={effectiveConnection} />;
  }
 if (restApi.includes(dataSourceType as typeof restApi[number])) {
  return <RestApiFields type={dataSourceType as typeof restApi[number]} connection={effectiveConnection} />;
}
  if (vectorSearchTypes.includes(dataSourceType as typeof vectorSearchTypes[number])) {
    return <VectorSearchDbFields type={dataSourceType as typeof vectorSearchTypes[number]} connection={effectiveConnection} />;
  }
  return null;
};

  const sourceTypeIcons: Record<string, React.ComponentType<any>> = {
    postgresql: SiPostgresql,
    mysql: SiMysql,
    sqlserver: SiMicrosoftsqlserver,
    mongodb: SiMongodb,
    redis: SiRedis,
    rest: Globe,
    elastic: SiElasticsearch,
    milvus: SiMilvus,
    Oracle: SiOracle,
    GraphQL: SiGraphql,
    Kafka: SiApachekafka,
    File: FileText,
    'FTP/SFTP': Globe,
  };

  const sourceTypeColors: Record<string, string> = {
    postgresql: 'text-[#336791]',
    mysql: 'text-[#4479A1]',
    sqlserver: 'text-[#CC2927]',
    mongodb: 'text-[#47A248]',
    redis: 'text-[#DC382D]',
    rest: 'text-gray-700',
    elastic: 'text-[#FEC514]',
    milvus: 'text-[#00A1EA]',
    Oracle: 'text-[#F80000]',
    GraphQL: 'text-[#E10098]',
    Kafka: 'text-gray-800',
    File: 'text-gray-700',
    'FTP/SFTP': 'text-gray-700',
  };

  const getSourceTypeIcon = (value: string, type: string) => {
    return sourceTypeIcons[value] || sourceTypeIcons[type] || Database;
  };

  const getSourceTypeColor = (value: string, type: string) => {
    return sourceTypeColors[value] || sourceTypeColors[type] || 'text-gray-700';
  };

  const SourceTypeOption = ({
    type, active, value, selected, onSelect
  }: {
    type: string;
    active: boolean;
    value: string;
    selected: boolean;
    onSelect: (value: string) => void;
  }) => {
    const id = `source-${type.replace(/\s+/g, '-').toLowerCase()}`;
    const Icon = getSourceTypeIcon(value, type);
    const color = active ? getSourceTypeColor(value, type) : 'text-gray-400';

    return (
        <label
        htmlFor={id}
        className={`p-3 border-2 rounded-lg transition-all duration-200 text-left cursor-pointer ${selected
          ? 'border-gray-500 bg-gray-50'
          : active
            ? 'border-gray-200 hover:border-gray-300 hover:bg-gray-50'
            : 'border-gray-100 bg-gray-50 cursor-not-allowed opacity-60'
        }`}
      >
            <input
          type="radio"
          id={id}
          name="source_type"
          value={value}
          disabled={!active}
          checked={selected}
          onChange={() => active && onSelect(value)}
          className="hidden"
        />
            <div className="flex items-center gap-2 pointer-events-none">
                <Icon className={`w-4 h-4 ${color}`} />
                <div>
                    <div className="text-sm font-medium text-gray-900">{type}</div>
                </div>
            </div>
        </label>
    );
  };

  const renderSourceTypeSelector = () => {
    const options = [
      {
        type: 'PostgreSQL',
        value: 'postgresql',
        active: true
      },
      {
        type: 'MySQL',
        value: 'mysql',
        active: true
      },
      {
        type: 'SQL Server',
        value: 'sqlserver',
        active: true
      },
      {
        type: 'MongoDB',
        value: 'mongodb',
        active: true
      },
      {
        type: 'Redis',
        value: 'redis',
        active: true
      },
      {
        type: 'REST API',
        value: 'rest',
        active: true
      },
      {
        type: 'Elasticsearch',
        value: 'elastic',
        active: true
      },
      {
        type: 'Milvus',
        value: 'milvus',
        active: true
      },
      {
        type: 'File',
        active: false
      },
      {
        type: 'Oracle',
        active: false
      },
      {
        type: 'GraphQL',
        active: false
      },
      {
        type: 'FTP/SFTP',
        active: false
      },
      {
        type: 'Kafka',
        active: false
      },
    ];

    return (
        <div className="grid grid-cols-2 md:grid-cols-3 lg:grid-cols-4 gap-3">
            {options.map(({
 type, active, value
}) => (
    <SourceTypeOption
            key={type}
            type={type}
            value={value}
            active={active}
            selected={dataSourceType === value}
            onSelect={setDataSourceType}
          />
        ))}
        </div>
    );
  };

  return (
      <TooltipProvider>
          <div className="p-8 bg-white/80 backdrop-blur-sm rounded-3xl border border-white/20">
              <form ref={formRef} onSubmit={handleSave} className="space-y-6 py-4 h-full">
                  {successBanner && (
                      <Alert className="border-green-200 bg-green-50">
                          <CheckCircle2 className="h-4 w-4 text-green-600" />
                          <AlertDescription className="text-green-800">{successBanner}</AlertDescription>
                      </Alert>
                  )}
                  <div className="flex items-center justify-between">
                      <h2 className="text-3xl font-bold text-black flex items-center gap-4">
                          <div>
                              <Settings className="w-7 h-7" />
                          </div>
                          {editingConnection ? 'Edit Connection' : 'New Connection'}
                      </h2>
                      <Tooltip>
                          <button
                onClick={onCancel}
                className="p-3 text-gray-400 hover:text-gray-600 hover:bg-white/50 rounded-xl transition-all duration-200 shadow-sm"
              >
                              <X className="w-5 h-5" />
                          </button>
                      </Tooltip>
                  </div>

                  {/* Basic Information */}
                  <div className="space-y-2">
                      <h3 className="text-xl font-bold text-gray-900 flex items-center gap-3">
                          <div className="w-2 h-8 bg-black rounded-full"></div>
                          Basic Information
                      </h3>
                      <div className="space-y-2">
                          <TooltipProvider>
                              <Label htmlFor="connection-name" className="select-none text-xs">
                                  Connection Name
                                  <Tooltip>
                                      <TooltipTrigger asChild>
                                          <Button variant="ghost" size="icon">
                                              <Info className="h-4 w-4" />
                                          </Button>
                                      </TooltipTrigger>
                                      <TooltipContent className="w-[300px]">
                                          <p>Enter a unique name for the connection.</p>
                                      </TooltipContent>
                                  </Tooltip>
                              </Label>
                          </TooltipProvider>
                          <Input
                id="connection-name"
                name="connection-name"
                placeholder="e.g., Production DB (Read-Only)"
                required
                defaultValue={editingConnection?.connection_name || initialValues?.connection_name}
              />
                      </div>
                      <div className="space-y-2">
                          <div className="space-y-2">
                              <TooltipProvider>
                                  <Label htmlFor="description" className="select-none text-xs">
                                      Description (Optional)
                                      <Tooltip>
                                          <TooltipTrigger asChild>
                                              <Button variant="ghost" size="icon">
                                                  <Info className="h-4 w-4" />
                                              </Button>
                                          </TooltipTrigger>
                                          <TooltipContent className="w-[300px]">
                                              <p>Optional: Provide a description for the connection.</p>
                                          </TooltipContent>
                                      </Tooltip>
                                  </Label>
                              </TooltipProvider>
                              <Textarea
                id="description"
                name="description"
                placeholder="e.g., Connection to the main production PostgreSQL database for analytics."
                defaultValue={editingConnection?.description || initialValues?.description}
              />
                          </div>
                      </div>
                  </div>

                  {/* Data Source Type */}
                  <div className="space-y-2">
                      <h3 className="text-xl font-bold text-gray-900 flex items-center gap-3">
                          <div className="w-2 h-8 bg-black rounded-full"></div>
                          Data Source Type
                      </h3>
                      <div className="space-y-2">
                          <Label htmlFor="source_type">
                              Data Source Type
                              <Tooltip>
                                  <span>Select the type of data source you are connecting to (e.g., PostgreSQL, MongoDB, etc.).</span>
                                  <Info className="inline-block w-4 h-4 text-gray-500 ml-2 cursor-pointer hover:text-gray-800" />
                              </Tooltip>
                          </Label>
                          {renderSourceTypeSelector()}
                      </div>
                  </div>

                  {/* Connection Details */}
                  {dataSourceType && (
                  <>
                      <div className="space-y-2">
                          <h3 className="text-xl font-bold text-gray-900 flex items-center gap-3">
                              <div className="w-2 h-8 bg-black rounded-full"></div>
                              Connection Details
                          </h3>
                          <div className="space-y-2">{renderConnectionDetails()}</div>
                      </div>

                      {/* Advanced Options */}
                      <div className="space-y-2">
                          <h3 className="text-xl font-bold text-gray-900 flex items-center gap-3">
                              <div className="w-2 h-8 bg-black rounded-full"></div>
                              Advanced Options
                          </h3>
                          <div className="grid grid-cols-1 md:grid-cols-2 gap-6">
                              <div className="space-y-2">
                                  <Label htmlFor="retry-attempts">
                                      Retry Attempts
                                      <Tooltip>
                                          <Info className="inline-block w-4 h-4 text-gray-500 ml-2 cursor-pointer hover:text-gray-800" />
                                      </Tooltip>
                                  </Label>
                                  <Input
                      id="retry-attempts"
                      name="retry_attempts"
                      type="number"
                      defaultValue={editingConnection?.advancedOptions?.retry_attempts || '3'}
                    />
                              </div>
                              <div className="space-y-2">
                                  <Label htmlFor="retry-backoff">
                                      Retry Backoff (seconds)
                                      <Tooltip>
                                          <Info className="inline-block w-4 h-4 text-gray-500 ml-2 cursor-pointer hover:text-gray-800" />
                                      </Tooltip>
                                  </Label>
                                  <Input
                      id="retry-backoff"
                      name="retry_backoff"
                      type="number"
                      defaultValue={editingConnection?.advancedOptions?.retry_backoff_secs || '2'}
                    />
                              </div>
                              <div className="space-y-2">
                                  <Label htmlFor="connection_pool_size">
                                      Connection Pool Size
                                      <Tooltip>
                                          <Info className="inline-block w-4 h-4 text-gray-500 ml-2 cursor-pointer hover:text-gray-800" />
                                      </Tooltip>
                                  </Label>
                                  <Input
                      id="connection_pool_size"
                      name="connection_pool_size"
                      type="number"
                      defaultValue={editingConnection?.advancedOptions?.connection_pool_size || '10'}
                    />
                              </div>
                              <div className="flex items-center space-x-2">
                                  <Checkbox
                      id="logging"
                      name="logging"
                      defaultChecked={editingConnection?.advancedOptions?.enable_logging}
                    />
                                  <Label htmlFor="logging" className="font-normal">
                                      Enable verbose logging of all requests and queries.
                                  </Label>
                              </div>
                              <div className="flex items-center space-x-2">
                                  <Checkbox
                      id="cache_responses"
                      name="cache_responses"
                      defaultChecked={editingConnection?.advancedOptions?.cache_responses}
                    />
                                  <Label htmlFor="cache_responses" className="font-normal">
                                      Cache responses to reduce repeated queries.
                                  </Label>
                              </div>
                          </div>
                      </div>

                      {/* Test Connection and Save/Cancel buttons */}
                      <div className="space-y-4">
                          {testResult.status !== 'idle' && (
                          <Alert variant={testResult.status === 'error' ? 'destructive' : 'default'}>
                              {testResult.status === 'success' && <CheckCircle2 className="h-4 w-4" />}
                              {testResult.status === 'error' && <XCircle className="h-4 w-4" />}
                              {testResult.status === 'testing' && <Loader2 className="h-4 w-4 animate-spin" />}
                              <AlertTitle>
                                  {testResult.status === 'testing' && 'Testing...'}
                                  {testResult.status === 'success' && 'Success'}
                                  {testResult.status === 'error' && 'Error'}
                              </AlertTitle>
                              <AlertDescription>{testResult.message}</AlertDescription>
                          </Alert>
                )}

                          {saveResult.status !== 'idle' && (
                          <Alert variant={saveResult.status === 'error' ? 'destructive' : 'default'}>
                              {saveResult.status === 'success' && <CheckCircle2 className="h-4 w-4" />}
                              {saveResult.status === 'error' && <XCircle className="h-4 w-4" />}
                              {saveResult.status === 'saving' && <Loader2 className="h-4 w-4 animate-spin" />}
                              <AlertTitle>
                                  {saveResult.status === 'saving' && 'Saving...'}
                                  {saveResult.status === 'success' && 'Saved Successfully'}
                                  {saveResult.status === 'error' && 'Save Failed'}
                              </AlertTitle>
                              <AlertDescription>{saveResult.message}</AlertDescription>
                          </Alert>
                )}

                          <div className={'flex w-full justify-between items-center gap-2'}>
                              <Button type="button" onClick={() => editingConnection && handleTestConnection(editingConnection)} disabled={testResult.status === 'testing'}>
                                  {testResult.status === 'testing' && <Loader2 className="mr-2 h-4 w-4 animate-spin" />}
                                  Test Connection
                              </Button>

                              <div className="flex gap-2">
                                  <Button type="button" variant="outline" onClick={onCancel}>
                                      Cancel
                                  </Button>
                                  <Button
                      type="submit"
                      className="bg-neutral-900 text-neutral-50 hover:bg-neutral-800"
                      disabled={saveResult.status === 'saving'}
                    >
                                      {saveResult.status === 'saving' ? (
                                          <Loader2 className="mr-2 h-4 w-4 animate-spin" />
                      ) : (
                          <Save className="mr-2 h-4 w-4" />
                      )}
                                      {editingConnection ? 'Update Connection' : 'Save Connection'}
                                  </Button>
                              </div>
                          </div>
                      </div>
                  </>
          )}
              </form>
          </div>
      </TooltipProvider>
  );
};
